import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import axiosInstance from '../../main/initializers/axios';
import { ApiConstants } from '../../main/constants/ApiConstants';
import GeneralInput from '../../main/components/generalInput/GeneralInput';
import GeneralModal from '../../main/components/modal/general/GeneralModal';
import GeneralTable from '../../main/components/grid/GeneralTable';
import BasicCli from '../../main/components/basicCli/BasicCli';
import './AdminProductMenuDetails.css';

type ProductMenuAdminItemDto = {
  productId: number;
  name: string;
  price: number;
  hasMenuDetails: boolean;
  descriptionAl?: string | null;
  descriptionEn?: string | null;
  descriptionIt?: string | null;
  isPrefered: boolean;
  order: number;
  isActive: boolean;
};

type UpsertDto = {
  productId: number;
  descriptionAl?: string | null;
  descriptionEn?: string | null;
  descriptionIt?: string | null;
  isPrefered: boolean;
  order: number;
  isActive: boolean;
};

type ProductMenuAdminTableItem = ProductMenuAdminItemDto & {
  priceLabel: string;
  hasMenuDetailsLabel: string;
  isPreferedLabel: string;
  isActiveLabel: string;
};

const AdminProductMenuDetails = () => {
  const { t } = useTranslation();
  const loggedUser = useSelector((s: any) => s.user?.user || s.user?.loggedUser || s.user);
  const companyId = Number(loggedUser?.companyId ?? loggedUser?.CompanyId ?? loggedUser?.companyID ?? loggedUser?.CompanyID ?? 0);

  const [data, setData] = useState<ProductMenuAdminItemDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<ProductMenuAdminItemDto | null>(null);
  const [addProductId, setAddProductId] = useState<number>(0);

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<UpsertDto>({
    defaultValues: {
      productId: 0,
      descriptionAl: '',
      descriptionEn: '',
      descriptionIt: '',
      isPrefered: false,
      order: 0,
      isActive: true,
    },
  });

  const columns = useMemo(
    () => [
      { entity: 'name', label: 'Name', isShown: true },
      { entity: 'priceLabel', label: 'Price', isShown: true },
      { entity: 'hasMenuDetailsLabel', label: 'HasDetails', isShown: true },
      { entity: 'isPreferedLabel', label: 'Prefered', isShown: true },
      { entity: 'order', label: 'Order', isShown: true },
      { entity: 'isActiveLabel', label: 'Active', isShown: true },
    ],
    [],
  );

  const tableData = useMemo<ProductMenuAdminTableItem[]>(
    () =>
      data.map((x) => ({
        ...x,
        priceLabel: Number(x.price || 0).toFixed(2),
        hasMenuDetailsLabel: x.hasMenuDetails
          ? t('menuDetails.hasDetailsYes', { defaultValue: 'Yes' })
          : t('menuDetails.hasDetailsNo', { defaultValue: 'No' }),
        isPreferedLabel: x.isPrefered
          ? t('menuDetails.hasDetailsYes', { defaultValue: 'Yes' })
          : t('menuDetails.hasDetailsNo', { defaultValue: 'No' }),
        isActiveLabel: x.isActive
          ? t('menuDetails.hasDetailsYes', { defaultValue: 'Yes' })
          : t('menuDetails.hasDetailsNo', { defaultValue: 'No' }),
      })),
    [data, t],
  );
  const productsWithoutDetails = useMemo(() => data.filter((x) => !x.hasMenuDetails), [data]);
  const nextOrder = useMemo(() => {
    const maxOrder = data.filter((x) => x.hasMenuDetails).reduce((acc, item) => Math.max(acc, Number(item.order || 0)), 0);
    return maxOrder + 1;
  }, [data]);

  useEffect(() => {
    if (!productsWithoutDetails.length) {
      setAddProductId(0);
      return;
    }

    const exists = productsWithoutDetails.some((x) => x.productId === addProductId);
    if (!exists) {
      setAddProductId(productsWithoutDetails[0].productId);
    }
  }, [productsWithoutDetails, addProductId]);

  const [pagination, setPagination] = useState<any>({
    page: 1,
    itemPerPage: 50,
    count: 0,
    itemsPerPage: [10, 25, 50, 100],
  });

  const load = async (pg = pagination) => {
    setError(null);

    if (!companyId) {
      setData([]);
      setPagination({ ...pg, count: 0 });
      setLoading(false);
      setError('CompanyId nuk u gjet nga user store.');
      return;
    }

    setLoading(true);
    try {
      const res = await axiosInstance.get<ProductMenuAdminItemDto[]>(ApiConstants.productMenuDetails.adminList, { params: { companyId } });

      const list = res.data || [];
      setData(list);
      setPagination({ ...pg, count: list.length });
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Gabim ne API';
      setError(String(msg));
      setData([]);
      setPagination({ ...pg, count: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const onEdit = (item: ProductMenuAdminItemDto) => {
    const isNew = !item.hasMenuDetails;
    setSelected(item);
    reset({
      productId: item.productId,
      descriptionAl: isNew ? '' : item.descriptionAl ?? '',
      descriptionEn: isNew ? '' : item.descriptionEn ?? '',
      descriptionIt: isNew ? '' : item.descriptionIt ?? '',
      isPrefered: isNew ? false : item.isPrefered ?? false,
      order: isNew ? nextOrder : item.order ?? 0,
      isActive: isNew ? true : item.isActive ?? true,
    });
    setOpen(true);
  };

  const onDelete = async (item: ProductMenuAdminItemDto) => {
    if (!companyId) return;
    if (!item.hasMenuDetails) return;
    await axiosInstance.delete(ApiConstants.productMenuDetails.delete, {
      params: { companyId, productId: item.productId },
    });
    await load();
  };

  const onAdd = () => {
    const candidate = productsWithoutDetails.find((x) => x.productId === addProductId);
    if (!candidate) return;
    onEdit(candidate);
  };

  const onSave = async (dto: UpsertDto) => {
    if (!companyId) return;
    await axiosInstance.post(ApiConstants.productMenuDetails.upsert, dto, { params: { companyId } });
    setOpen(false);
    setSelected(null);
    await load();
  };

  if (loading) return <div style={{ padding: 16 }}>{t('menuDetails.loading', { defaultValue: 'Loading...' })}</div>;
  if (error) return <div style={{ padding: 16, color: 'red' }}>{error}</div>;

  return (
    <BasicCli>
      <div className="amdWrap bcPageContent">
        <div className="amdContainer">
          <div className="bcPageHeader">
            <h1 className="bcPageTitle">{t('menu.menuDetails', { defaultValue: 'MenuDetails' })}</h1>
            <div className="bcPageHeaderActions amdHeaderActions">
              <select
                className="ui-control ui-select amdProductSelect"
                value={addProductId || ''}
                onChange={(e) => setAddProductId(Number(e.target.value))}
                disabled={!productsWithoutDetails.length}
              >
                {productsWithoutDetails.length ? (
                  productsWithoutDetails.map((item) => (
                    <option key={item.productId} value={item.productId}>
                      {item.name}
                    </option>
                  ))
                ) : (
                  <option value="">{t('menuDetails.allProductsHaveDetails')}</option>
                )}
              </select>
              <button type="button" className="ui-control ui-btn ui-btn--primary" onClick={onAdd} disabled={!productsWithoutDetails.length}>
                {t('generic.add', { defaultValue: 'Add' })}
              </button>
            </div>
          </div>

          <div className="amdTable">
            <GeneralTable
              title=""
              data={tableData}
              columns={columns as any}
              gridEntity={999}
              hasRp
              pagination={pagination}
              selectedItem={() => {}}
              selectedItemPagination={(pg: any) => setPagination(pg)}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          </div>
        </div>

        {open && selected && (
          <GeneralModal
            headerName={`${t(selected.hasMenuDetails ? 'menuDetails.editTitle' : 'menuDetails.addTitle')}: ${selected.name}`}
            width="720px"
            radius="12px"
            handleClose={() => {
              setOpen(false);
              setSelected(null);
            }}
            handleClick={handleSubmit(onSave)}
          >
            <div style={{ display: 'grid', gap: 12 }}>
              <GeneralInput
                label={t('menuDetails.descriptionAl')}
                name="descriptionAl"
                type="text"
                placeholder={t('menuDetails.descriptionAlPlaceholder')}
                control={control as any}
                errors={errors as any}
              />
              <GeneralInput
                label={t('menuDetails.descriptionEn')}
                name="descriptionEn"
                type="text"
                placeholder={t('menuDetails.descriptionEnPlaceholder')}
                control={control as any}
                errors={errors as any}
              />
              <GeneralInput
                label={t('menuDetails.descriptionIt')}
                name="descriptionIt"
                type="text"
                placeholder={t('menuDetails.descriptionItPlaceholder')}
                control={control as any}
                errors={errors as any}
              />
              <GeneralInput
                label={t('menuDetails.isPrefered', { defaultValue: 'Is Prefered' })}
                name="isPrefered"
                type="checkbox"
                placeholder=""
                control={control as any}
                errors={errors as any}
              />
              <GeneralInput
                label={t('menuDetails.order', { defaultValue: 'Order' })}
                name="order"
                type="number"
                placeholder="0"
                control={control as any}
                errors={errors as any}
              />
              <GeneralInput
                label={t('menuDetails.isActive', { defaultValue: 'Is Active' })}
                name="isActive"
                type="checkbox"
                placeholder=""
                control={control as any}
                errors={errors as any}
              />
            </div>
          </GeneralModal>
        )}
      </div>
    </BasicCli>
  );
};

export default AdminProductMenuDetails;
